import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@material-ui/core';
import { HeadCell, InlineKeyboardDatePicker, TableCell, TableHeadSearchField, TableHeadSelect, Typography } from '../../../../ui-kit';
import { Supplier } from '../../../../generated/graphql';
import { useUser } from '../../../../utils/hooks/auth';
import { getDateFormatByLocale } from '../../../../utils/helpers/getDateFormatByLocale';
import { DeliveryList } from './types';

interface DeliveryListTableHeadCellProps {
    index: number;
    headCell: HeadCell<DeliveryList>;
    suppliers: Pick<Supplier, 'id' | 'legalName'>[];
    deliveryDateUtc: null | Date;
    setDeliveryDateUtc(date: Date | null): void;
    supplierId: string | null;
    setSupplierId(supplierId: string | null): void;
    invoiceNumber: string;
    setInvoiceNumber(invoiceNumber: string): void;
}

const DeliveryListTableHeadCell = ({
    index, headCell, suppliers,
    deliveryDateUtc, setDeliveryDateUtc,
    supplierId, setSupplierId,
    invoiceNumber, setInvoiceNumber,
}: DeliveryListTableHeadCellProps) => {
    const { t } = useTranslation();
    const { user: { cultureCode } } = useUser();
    const dateFormat = getDateFormatByLocale(cultureCode);

    let headCellContent;
    switch (headCell.id) {
        case 'invoiceNumber':
            headCellContent = (
                <TableHeadSearchField
                    margin='dense'
                    value={invoiceNumber}
                    onChange={setInvoiceNumber}
                    placeholder={t(...headCell.label)}
                />
            );
            break;
        case 'supplier': {
            const options = suppliers.map(({ id, legalName }) => ({ id, name: legalName }));

            headCellContent = (
                <TableHeadSelect
                    label={t(...headCell.label)}
                    onChange={setSupplierId}
                    fullWidth
                    options={options}
                    value={supplierId}
                />
            );
            break;
        }
        case 'deliveryDateUtc': {
            headCellContent = (
                <InlineKeyboardDatePicker
                    placeholder={t(...headCell.label)}
                    onChange={setDeliveryDateUtc}
                    clearable
                    value={deliveryDateUtc}
                    format={dateFormat}
                    fullWidth
                />
            );
            break;
        }
        default:
            headCellContent = <Typography uppercase color='textSecondary' variant='caption'>{t(...headCell.label)}</Typography>;
            break;
    }

    const isSticky = index === 0;
    return (
        <TableCell
            id={headCell.id as string}
            key={headCell.id as string}
            align={headCell.align}
            isTableHead
            isSticky={isSticky}
            padding='none'
        >
            <Box px={2}>
                {headCellContent}
            </Box>
        </TableCell>
    );
};

export default DeliveryListTableHeadCell;
